/**
 * Battle Cards — SINGLE SOURCE OF TRUTH for meld legality and the copies ladder.
 *
 * Pure, RNG-free logic, exactly as gesture legality lives only in `combatActionRules.ts`.
 * The backend owns all randomness (shuffling, drawing, binary rolls); nothing here draws,
 * shuffles, or mutates.
 *
 * THE MELD RULE, as `/battle-cards` states it — the page is the requirement, not any older
 * spec in `docs/superpowers/specs/`:
 *
 * > "Play a meld of 1-4 copies of one card of that gesture, or play bare."
 * > "A meld must contain at least one card of the gesture you committed, and only cards of
 * >  that gesture do anything at all. An off-gesture card is not a smaller contribution — it
 * >  is nothing."
 *
 * Both sentences hold at once, which splits the rule in two:
 *
 * - **Legality is permissive** ({@link isMeldLegal}): at least one card of the committed
 *   gesture, nothing bigger than a hand, and every card actually held. Junk may ride along.
 * - **Strength is strict** ({@link getMeldStrength}): only the biggest stack of ONE card of
 *   the committed gesture counts. Everything else is worth exactly zero.
 *
 * An EMPTY meld is always legal: committing a bare gesture is how every cards-off battle
 * plays, and the card layer must never make a gesture unplayable.
 *
 * Design: requirements/BATTLE_CARDS_V4_LOADOUTS.md
 */

import { calculateBleedPerTurn } from '../constants/battleConstants.js';
import {
  CARD_MATCHED_ELEMENT_MAX_BONUS,
  CARD_MATCHED_ELEMENT_MULTIPLIER,
  EFFECT_COPY_MULTIPLIERS,
  EFFECT_FULL_STACK_COPIES,
  EFFECT_HAND_SIZE,
  EFFECT_MELD_MAX,
  FULL_COMMIT_BLOW_MULTIPLIER,
  RECOVER_MELD_HEAL_BONUS,
  ROUND_BOOST_COPIES,
} from '../constants/cardConstants.js';
import {
  BASE_CARDS,
  EFFECT_CARD_CATALOGUE,
  EFFECT_DECK_GESTURES,
  getEffectCardById,
  isHealingCard,
  isSuikamonCard,
} from './cardCatalog.js';
import { EVOLUTION_CARDS_PER_STEP } from './battleEvolution.js';
import { COMBAT_SPECIAL_EFFECT } from './petModels.js';
import type { ElementId } from './petModels.js';
import { isGestureSlot } from './cardTypes.js';
import type {
  CardPreview,
  EffectCard,
  EffectCardGesture,
  EffectCardId,
  MeldLegality,
  MeldStrength,
} from './cardTypes.js';

/**
 * Every card a hand can hold, in the order a commit lists them.
 *
 * The sixteen SKILLS first and then the four empty slots, because the catalogue is the deck's
 * own order and a base card is not in it — deliberately, it is not a skill (see
 * `cardCatalog.ts`). A commit still has to be able to name one, so this is the one list that
 * holds both, and it is a constant rather than a filter per call so the order can never depend
 * on when it was asked for.
 */
const COMMITTABLE_CARDS: readonly EffectCard[] = [...EFFECT_CARD_CATALOGUE, ...BASE_CARDS];

/**
 * Is this meld playable with the committed gesture, out of this hand?
 *
 * `hand` is optional so the frontend can validate a proposed meld it is still assembling;
 * the backend always passes it, because the authoritative check is "you actually hold
 * these cards" and a client is never trusted for that.
 */
export function isMeldLegal(
  cardIds: readonly EffectCardId[],
  committedGesture: EffectCardGesture,
  hand?: readonly EffectCardId[]
): MeldLegality {
  if (cardIds.length === 0) return { legal: true };

  // A meld is played out of a hand, so a hand is the ceiling. It is NOT EFFECT_MELD_MAX:
  // only the scoring stack is capped at four, and the page explicitly allows carrying
  // off-gesture cards that do nothing.
  if (cardIds.length > EFFECT_HAND_SIZE) {
    return { legal: false, reason: 'too-many-cards' };
  }

  const cards = cardIds.map((id) => getEffectCardById(id));
  if (cards.some((card) => card === undefined)) {
    return { legal: false, reason: 'unknown-card' };
  }

  if (!cards.some((card) => card!.gesture === committedGesture)) {
    return { legal: false, reason: 'no-committed-gesture-card' };
  }

  if (hand) {
    for (const id of new Set(cardIds)) {
      if (countCopies(hand, id) < countCopies(cardIds, id)) {
        return { legal: false, reason: 'not-in-hand' };
      }
    }
  }

  return { legal: true };
}

/** How many copies of one card a list of ids holds. */
export function countCopies(cardIds: readonly EffectCardId[], cardId: EffectCardId): number {
  return cardIds.reduce((total, id) => (id === cardId ? total + 1 : total), 0);
}

/**
 * **THE LADDER, RESOLVED IN ONE PLACE — what N copies of THIS card multiply its number by.**
 *
 * It exists because the ladder stopped being the same for every card. `EFFECT_COPY_MULTIPLIERS`
 * is ×N below a full stack (which pays `EFFECT_FULL_STACK_MULTIPLIER`) and a healing card now pays
 * {@link RECOVER_MELD_HEAL_BONUS} on top of it at the two middle rungs, and there are THREE call sites that turn copies into a number: the fight
 * (`resolveMeld`), the card face ({@link getCardPreview}) and the AI's own score (`scoreCard`).
 * Two of those three read the raw copy count rather than the ladder, which was harmless while
 * the two agreed and is exactly how a face comes to print a number the fight does not use.
 *
 * So the rider goes HERE, in the one function all three can call, rather than into any of them.
 * For every card that is not a healing card this returns the flat rung it always did.
 *
 * A copy count off the ladder — zero, or past a full stack — is worth nothing, the same answer
 * {@link getMeldStrength} has always given an illegal meld.
 */
export function getMeldMultiplier(card: Pick<EffectCard, 'effect'>, copies: number): number {
  const rung = EFFECT_COPY_MULTIPLIERS.find((row) => row.copies === copies);
  if (!rung) return 0;
  if (!isHealingCard(card)) return rung.multiplier;

  return rung.multiplier * (RECOVER_MELD_HEAL_BONUS[copies] ?? 1);
}

/**
 * **WHAT A MELD OF `copies` OF THIS CARD IS WORTH, IN WHOLE UNITS — the one number both the
 * fight and the card face read.**
 *
 * The expression was written out twice — once in `resolveMeld` (the fight) and once in
 * {@link getCardPreview} (the face) — and the two agreed only because nobody had changed
 * either. That is the drift this repo keeps finding, so it is one function now, and the AI's
 * own price ({@link scoreCard}) reads it too.
 *
 * **AND IT ROUNDS, which is the bug it was extracted to fix.** {@link RECOVER_MELD_HEAL_BONUS}
 * is fractional at the middle rungs (×2.5 at three copies), so an odd card value came out on a
 * half: three Rare Heals healed **82.5 HP**, the battle log printed *"restores 82.5 HP"*, and
 * the fighter stood on **197.5 of 200** with the bar's own label printing the fraction. HP is
 * whole everywhere else in the game — every skill, every bleed tick, every evolution heal — and
 * a health bar is not a place to do arithmetic with halves.
 *
 * Rounded HERE rather than at each call site, because the face and the fight rounding
 * differently is exactly the same class of defect as the two of them multiplying differently:
 * a card that prints 83 and heals 82.5 is a card lying about itself by half a point.
 *
 * `applyMatchedElement` already rounds a MATCHED card (it has since the cap went per-copy); an
 * unmatched one fell straight through, which is why the fraction only ever showed on the cards
 * whose element did not line up.
 */
export function getMeldValue(
  card: EffectCard,
  copies: number,
  /** The swinging pet's element, for STAB. Undefined where there is no pet — the unmatched face. */
  ownElement?: ElementId
): number {
  return Math.round(
    applyMatchedElement(card, ownElement, (card.value ?? 0) * getMeldMultiplier(card, copies), copies)
  );
}

/**
 * How big a meld plays: **the biggest stack of one card of the committed gesture**.
 *
 * Off-gesture cards and lone singletons of other cards are worth zero — "an off-gesture card
 * is not a smaller contribution, it is nothing". Reading only the biggest stack is also what
 * makes the odds table honest: it is tabulated by each hand's CEILING, not by its total.
 *
 * An illegal or empty meld is worth nothing rather than throwing, so a caller that skipped
 * {@link isMeldLegal} degrades to "no cards played" instead of crashing a battle mid-turn.
 */
export function getMeldStrength(
  cardIds: readonly EffectCardId[],
  committedGesture: EffectCardGesture,
  /**
   * This suit is BOOSTED this round — the stack plays one copy bigger.
   *
   * Applied here, in the one function that decides how big a meld is, so the card face, the
   * AI's score, the exchange's tie-break and the round result all read one number. Defaults to
   * false, which is a round with no boost on this suit and every caller that predates it.
   */
  boosted = false
): MeldStrength {
  const none: MeldStrength = { cardId: null, tier: null, copies: 0, multiplier: 0 };
  if (cardIds.length === 0 || !isMeldLegal(cardIds, committedGesture).legal) return none;

  let bestId: EffectCardId | null = null;
  let bestCopies = 0;

  // **COMMITTABLE order, not played order** — and base cards LAST, which is what the constant's
  // own shape (`[...catalogue, ...BASE_CARDS]`) already guarantees. The same cards must always
  // score the same way, and the catalogue lists Rares before Commons, so a tie between two
  // printings of the same effect prefers the Rare — the same preference the tie rule applies,
  // and by the same token a tie between a skill and an empty slot prefers the skill.
  //
  // Base cards are in this walk at all because they now HAVE a number (`BASE_CARD_CHIP`):
  // a meld of nothing but empty slots used to score `none` and resolve to nothing, which is
  // exactly the "the card layer does not exist until you have collected something" hole the
  // base card's value closes.
  let bestCard: EffectCard | null = null;

  for (const card of COMMITTABLE_CARDS) {
    if (card.gesture !== committedGesture) continue;
    const copies = Math.min(countCopies(cardIds, card.id), EFFECT_MELD_MAX);
    if (copies > bestCopies) {
      bestCopies = copies;
      bestId = card.id;
      bestCard = card;
    }
  }

  if (bestId === null || bestCard === null) return none;

  // The round's boost lands HERE and only here — one more copy of the card being committed,
  // never past the top of the ladder.
  bestCopies = boostedCopies(bestCopies, boosted);

  // The ladder is flat ×N for everything the meld points at the OPPONENT — a full stack is four
  // times face value and the battle carries on, because a card that ends a fight regardless of
  // HP is not a bigger card but a different game. A HEALING card is the one exception, and it
  // is applied inside `getMeldMultiplier` rather than here so the face and the AI read the same
  // rung this does.
  return {
    cardId: bestId,
    // Carried, not looked up again downstream: the tie rule needs the tier and it is
    // already known here. See MeldStrength.
    tier: getEffectCardById(bestId)?.tier ?? null,
    copies: bestCopies,
    multiplier: getMeldMultiplier(bestCard, bestCopies),
  };
}

/**
 * **MATCHED ELEMENT (STAB): a card in your own colour hits harder, by a bounded amount.**
 *
 * Pure and shared, which is the requirement that shapes it — **the card face prints the number
 * the fight uses.** A UI that multiplied separately, or a fight that multiplied where the face
 * could not see, would be a card lying about its own number, which is worse than a card with
 * no number at all.
 *
 * Two rules, both from the requirement:
 *
 * - **Binary cards never match.** They carry no element at all (see `resolveCardElement`),
 *   because there is no magnitude to multiply and boosting the CHANCE instead would break the
 *   certainty invariant — four Rare copies is exactly 100%, and 125% of certainty is nothing.
 *   The whole magic suit is binary, so magic gets no STAB by construction; its synergy is the
 *   signature (`cardSynergy.ts`), which is a deliberate split and not an oversight.
 * - **The bonus is capped at {@link CARD_MATCHED_ELEMENT_MAX_BONUS} PER COPY**, which is about
 *   the engine's own element advantage on a Lv20 spell (`getElementAdvantageBonus`). A card rider is not allowed to outpay the mechanic
 *   it is named after — but the unit that comparison is made in is ONE CARD, not one meld.
 *
 *   It was a flat cap first, and the flat version quietly deleted the mechanic: a meld's value
 *   is already multiplied by its copies, so a four-copy stack arrived here at four times face
 *   value and had its rider truncated to a single card's worth. Stacks are where nearly all of
 *   a card's HP comes from, so the cap was binding precisely where the bonus was supposed to be
 *   felt, and criterion 9 measured the whole feature at **+0.4 ±1.2 points against a +2 floor**
 *   with the multiplier already raised to 1.5. Per copy, four matched cards may buy what four
 *   element advantages buy, and no more.
 *
 * The two element bonuses can never stack: element advantage applies only to magic
 * SKILL damage, and no magic card has a value.
 */
export function applyMatchedElement(
  card: EffectCard,
  ownElement: ElementId | undefined,
  value: number,
  /**
   * Copies in the meld, so the cap can be per card rather than per exchange.
   *
   * Defaults to one, which is what a caller holding a single card's face value means. A caller
   * that has already multiplied by the copies MUST pass them, or it is asking for a one-card
   * cap on a four-card number.
   */
  copies = 1
): number {
  if (card.element === undefined || ownElement === undefined || card.element !== ownElement) {
    return value;
  }

  const bonus = Math.min(
    value * (CARD_MATCHED_ELEMENT_MULTIPLIER - 1),
    CARD_MATCHED_ELEMENT_MAX_BONUS * Math.max(1, copies)
  );
  return Math.round(value + bonus);
}

/** Cards in a hand that could be played with this gesture at all. */
export function getPlayableCards(
  hand: readonly EffectCard[],
  gesture: EffectCardGesture
): EffectCard[] {
  return hand.filter((card) => card.gesture === gesture);
}

/**
 * **THE COMMIT RULE: your gesture plays every card of that gesture you hold.**
 *
 * A player does not pick cards — picking the gesture IS picking the cards. Everything in
 * hand wearing that suit goes in, and everything else stays in hand for the next round.
 * That is what makes the four action buttons readable: one tap has exactly one meaning, and
 * "what will this play?" is answerable from the hand alone, before you tap.
 *
 * It also gives the discard its job. The only way to keep a suited card out of a commit is
 * to pitch it first, so the once-per-round discard is now the shaping tool rather than a
 * mulligan you rarely bothered with.
 *
 * Scoring is unchanged and still strict — {@link getMeldStrength} counts only the biggest
 * stack of ONE card — so a suit's odd singletons ride along and are spent for nothing. That
 * cost is deliberate and it is the reason to pitch them.
 *
 * **A MELD COMES OUT OF THE HAND AND NOWHERE ELSE.** The round's shared BOOST
 * ({@link boostedCopies}) makes a committed stack play one copy bigger, but it puts no card on
 * the board: nothing joins a hand from outside it, so this list is exactly what is spent.
 *
 * **AND IT PLAYS THE EMPTY SLOTS TOO.** A base card ({@link isBaseCard}) is a card of its
 * suit sitting in the hand, so the commit rule takes it like every other card of that suit —
 * it simply scores nothing when it gets there.
 *
 * It used to be skipped, which made this function disagree with the screen that prices it:
 * `previewCommit` has always listed the suit's empty slots among what a commit SPENDS, so the
 * pitch step quoted a cost the engine then did not take. Skipping them also meant they were
 * never discarded and so never redrawn — an empty slot a hand drew stayed in it until the
 * player paid a pitch to be rid of it, on the one suit where committing should have cleared it
 * for free.
 *
 * And an empty slot COUNTS, like every other card — see {@link countPlayedCards}. It counted
 * for nothing while it was worth nothing; now that it lands `BASE_CARD_CHIP` a copy,
 * a card is a card.
 *
 * Deck order, not hand order: the same five cards must always commit the same list, or a
 * seeded battle would stop replaying.
 */
export function getCommittedMeld(
  hand: readonly EffectCardId[],
  gesture: EffectCardGesture
): EffectCardId[] {
  return COMMITTABLE_CARDS.filter((card) => card.gesture === gesture).flatMap((card) =>
    Array.from({ length: countCopies(hand, card.id) }, () => card.id)
  );
}

/**
 * **HOW MANY CARDS THE RULES COUNT — every card committed, empty slots included.**
 *
 * The two rules in {@link resolveExchangeByCards} are the only place a COUNT decides a round
 * rather than sizing it, and what they count is what a move put on the board. **A card is a
 * card.** Four empty slots is a full commit and takes the round; two empty slots beat one on a
 * mirror. The player who committed more cards committed more cards, and no screen has to
 * explain why some of the tiles it just drew were not counted.
 *
 * **IT USED TO EXCLUDE BASE CARDS, and that was right while they were worth nothing.** An empty
 * slot resolved to no outcome at all, so counting it would have let a pet that had collected
 * nothing out-card a pet that had, for free. That is no longer the trade being made: a base card
 * now lands `BASE_CARD_CHIP` per copy in its own suit's currency — damage on attack, magic and
 * dodge, HP on recover — so committing four of them spends four real cards out of a five-card
 * hand to land twelve — the cheapest full commit in the game and by a wide margin the weakest
 * one. The collection still buys everything it ever bought: a
 * collected suit lands three to five times what the blank does off the same four cards.
 *
 * This is now identical to `cardIds.length`, and it stays a named function on purpose — it is
 * the number three screens print as the evidence for a verdict (the move buttons, the commit
 * strip, the round-result panel) and the number the exchange is settled on, and those must go
 * on reading one definition rather than each counting an array their own way.
 */
export function countPlayedCards(cardIds: readonly EffectCardId[]): number {
  return cardIds.length;
}

/**
 * **WHAT EACH SUIT THIS HAND CAN ACTUALLY THROW — the count the move legality rule reads.**
 *
 * A commit spends every card of its suit, so "how many cards does attack play?" is simply how
 * many attack cards are in hand, counted by {@link countPlayedCards} so this number and the one
 * the exchange is settled on are one definition rather than two readings of the same array.
 * Empty slots included: a base card is a card, and a suit you have collected nothing for is
 * still a suit you can play.
 *
 * Every suit is present in the result, at zero when the hand holds none of it — which is the
 * whole point. `combatActionRules` turns a zero into "you cannot choose this move", and a
 * `Partial` that simply omitted the empty suits would make the difference between "no cards"
 * and "not measured" invisible at exactly the moment it decides the turn.
 *
 * An id the catalogue does not know is dropped rather than thrown on: this feeds a legality
 * check on a live turn, and a stale id must cost that suit a card, never the battle.
 */
export function countHandBySuit(
  cardIds: readonly EffectCardId[]
): Record<EffectCardGesture, number> {
  const counts = EFFECT_DECK_GESTURES.reduce(
    (acc, gesture) => ({ ...acc, [gesture]: 0 }),
    {} as Record<EffectCardGesture, number>
  );

  for (const id of cardIds) {
    const gesture = getEffectCardById(id)?.gesture;
    // A Suikamon card is suitless (`SUIKAMON_CARD_SLOT`): it plays no move, so it counts
    // toward none of the four and can never make a move legal that the hand cannot throw.
    // `isGestureSlot` rather than a truthiness check, because the sentinel IS truthy and the
    // old line quietly grew a fifth key on the record.
    if (gesture && isGestureSlot(gesture)) {
      counts[gesture] = countPlayedCards([id]) + counts[gesture];
    }
  }

  return counts;
}

/**
 * **How many Suikamon cards this hand is holding.**
 *
 * The number the whole mechanic turns on: hold {@link EVOLUTION_CARDS_PER_STEP} of them into an
 * exchange and the Suikamon evolves. Counted here, off a hand, so the engine, the hand view,
 * the nameplate and the AI all read one definition — and so the count is a fact about the CARDS
 * a seat is holding rather than about anything the engine has already decided.
 */
export function countSuikamonCards(cardIds: readonly EffectCardId[]): number {
  return cardIds.filter((id) => isSuikamonCard(id)).length;
}

/**
 * The Suikamon cards a full set spends — the first {@link EVOLUTION_CARDS_PER_STEP} in the
 * hand, or none when the hand is short of a set.
 *
 * Returns the ids to spend rather than mutating, so the engine can decide an evolution and the
 * caller can spend the cards out of the hand exactly as it spends a committed meld. Copies
 * share one id, so *which* three is not a question anyone can ask.
 */
export function takeSuikamonSet(cardIds: readonly EffectCardId[]): EffectCardId[] {
  const held = cardIds.filter((id) => isSuikamonCard(id));
  return held.length >= EVOLUTION_CARDS_PER_STEP
    ? held.slice(0, EVOLUTION_CARDS_PER_STEP)
    : [];
}

/**
 * **THE ROUND'S BOOST, APPLIED: what a stack of `copies` actually plays at.**
 *
 * The one place the boost is added, so a card face, the AI, the exchange and the round result
 * cannot disagree about how big a meld was. Bounded by {@link EFFECT_MELD_MAX} — the top of the
 * ladder is the top of the ladder, boosted or not — and worth nothing on an empty stack: a
 * boost sizes a bet you made, it does not make one for you.
 */
export function boostedCopies(copies: number, boosted: boolean): number {
  if (copies === 0) return 0;
  return Math.min(EFFECT_MELD_MAX, copies + (boosted ? ROUND_BOOST_COPIES : 0));
}

/**
 * What committing a gesture out of this hand would do, for the UI to state up front.
 *
 * `fires` is the stack that actually scores; `spent` is what goes with it and does nothing.
 * A gesture the hand holds nothing of is a BARE commit — always legal, and the card layer
 * must never make a gesture unplayable.
 */
export function previewCommit(
  hand: readonly EffectCard[],
  gesture: EffectCardGesture,
  /** This suit is boosted this round — the commit plays one copy bigger. */
  boosted = false
): { meld: EffectCard[]; strength: MeldStrength; fires: EffectCard[]; spent: EffectCard[] } {
  const meld = hand.filter((card) => card.gesture === gesture);
  const strength = getMeldStrength(
    meld.map((card) => card.id),
    gesture,
    boosted
  );

  // `fires` lists CARDS, and the boost is not one — a boosted stack of two fires two cards and
  // lands at three copies, so the row of tiles never grows a card the player is not holding.
  const fires: EffectCard[] = [];
  const spent: EffectCard[] = [];
  for (const card of meld) {
    // Only the scoring card counts, and only up to the four-copy cap — a fifth copy of the
    // same card is as dead as an off-card singleton, so it reads as spent.
    if (card.id === strength.cardId && fires.length < strength.copies) fires.push(card);
    else spent.push(card);
  }

  return { meld, strength, fires, spent };
}

/**
 * The biggest meld a hand can make for a committed gesture, or an empty meld when the hand
 * holds nothing of that suit.
 *
 * Used by the UI to name the best play and by the backend AI to pick one. Ties on copy
 * count are broken by deck order, which is stable, so the same hand always proposes the
 * same meld — a battle stays reproducible.
 */
export function findBestMeld(
  hand: readonly EffectCardId[],
  committedGesture: EffectCardGesture
): EffectCardId[] {
  let best: EffectCardId[] = [];

  // Iterate the COMMITTABLE list, not the hand: iterating the hand would break ties by the order
  // the cards happened to be dealt, so the same five cards could propose two different melds.
  // Skills come first in that list and empty slots last, so a tie never proposes the blank.
  for (const card of COMMITTABLE_CARDS) {
    if (card.gesture !== committedGesture) continue;

    const copies = Math.min(countCopies(hand, card.id), EFFECT_MELD_MAX);
    if (copies > best.length) {
      best = Array.from({ length: copies }, () => card.id);
    }
  }

  return best;
}

/**
 * The best meld a hand of CARDS (not ids) can make, with its strength already resolved.
 *
 * The object-flavoured convenience over {@link findBestMeld}, for the UI and the reference
 * page, which hold cards rather than ids. Same answer, one call.
 */
export function getBestMeld(
  hand: readonly EffectCard[],
  gesture: EffectCardGesture,
  /** This suit is boosted this round — the stack lands one copy bigger. */
  boosted = false
): { cards: EffectCard[]; strength: MeldStrength } {
  const ids = findBestMeld(
    hand.map((card) => card.id),
    gesture
  );
  const byId = new Map(hand.map((card) => [card.id, card]));

  return {
    cards: ids.flatMap((id) => {
      const card = byId.get(id);
      return card ? [card] : [];
    }),
    strength: getMeldStrength(ids, gesture, boosted),
  };
}

/**
 * What a card lands at, for N copies — **stated as the fight states it.**
 *
 * Numeric cards multiply, ×N at every rung. Binary cards buy CERTAINTY a quarter at a
 * time — one copy is 25%, the full stack is 100% — and the turns branch below only comes
 * into play if {@link EFFECT_BINARY_BASE_CHANCE} is ever tuned above that ceiling.
 *
 * ONE CARD DOES NOT SIMPLY PRINT `value × copies`, and it is deliberate:
 *
 * - **A bleed prints its PER-TURN TICK.** The multiplied value is what the engine rolls
 *   with, but it is not a number the battle ever shows: the log reports the tick, the HP
 *   bar moves by the tick, and the card used to print the pool it was a quarter of. So a
 *   pair of Bleeds read `20` while dealing 5 a turn, and the only way to reconcile the two
 *   was arithmetic the player had no reason to know to do. It reads `5/turn` now — the
 *   duration is on the card's rules text, where a fact that never changes belongs.
 *
 * A healing card used to be the second exception, printing `FULL HP` at four copies. It
 * prints its number like everything else now — see {@link MeldStrength}.
 */
export function getCardPreview(
  card: EffectCard,
  copies: number,
  /**
   * The element of the pet holding the card, when it is known.
   *
   * Passing it is what makes the face honest: STAB is applied HERE, through the same pure
   * {@link applyMatchedElement} the engine calls, so the number printed is the number rolled.
   * Omit it in a context with no pet — the catalogue page listing all thirty-two cards — and
   * the card prints its unmatched face value.
   */
  ownElement?: ElementId
): CardPreview {
  // **THE BASE CARD PRINTS ITS CHIP, like everything else.** It has no special effect, but it
  // does have a magnitude now (`BASE_CARD_CHIP`), and this deck's faces state what the
  // fight rolls. The `none` kind is kept for a card that genuinely has no number at all — a
  // future effect-only printing — rather than deleted, so a face that gets one still degrades
  // to drawing no chip instead of to printing a misleading `0`.
  if (card.effect === COMBAT_SPECIAL_EFFECT.None && card.value === undefined) {
    return { kind: 'none', amount: 0, label: '' };
  }

  if (card.chance !== undefined) {
    const percent = Math.round(card.chance * copies * 100);
    if (percent <= 100) {
      return { kind: 'chance', amount: percent, label: `${percent}%` };
    }
    // Past certainty the surplus becomes duration: 200% is two turns. A surplus that is not a whole
    // turn (a full stack of Epics is 125%: certain, plus a 25% chance of one more turn) stays a
    // percentage, which is exactly what `rollBinaryCard` rolls — "1 turns" would hide the 25%.
    if (percent % 100 !== 0) return { kind: 'chance', amount: percent, label: `${percent}%` };
    const turns = percent / 100;
    return { kind: 'turns', amount: turns, label: `${turns} turns` };
  }

  /*
   * **A FULL STACK OF A HEALING CARD PRINTS THE OUTCOME, because its number is dead.**
   *
   * {@link EXCHANGE_OVERWHELM_CARDS} copies of a recover card IS a full recover commit, and the
   * engine settles that as `heal = max(cardHeal, missingHp)` and then clamps to the bar. Both
   * branches end at a full bar whatever the card's number is — so the number this face used to
   * print at the top rung could never be the number the fight used.
   *
   * It was wrong before `RECOVER_MELD_HEAL_BONUS` too; the bonus only made it VISIBLE, by
   * printing more at three copies than at four. The fix is not to inflate the top rung until the
   * lie is monotonic — it is to stop printing an amount where the fight has a destination.
   *
   * Deliberately keyed on the FULL-HEAL GESTURE and not merely on the effect: `isFullRecoverCommit`
   * requires the recover commit, so a healing card ever printed on another suit would have an
   * ordinary number at four copies and must keep printing it.
   *
   * **The recover BLANK is in here too, and by its unit rather than its effect.** Its effect is
   * `None`, so `isHealingCard` does not and should not claim it — that list is the one the meld
   * bonus is paid on. But four blanks out of an empty recover slot is a full recover commit like
   * any other, and the fight takes that bar to full: a face printing `12 HP` beside a commit
   * that heals to full is the same lie this branch exists to stop telling.
   */
  const landsHealing = isHealingCard(card) || card.unit === 'HP';
  if (
    landsHealing &&
    card.gesture === FULL_HEAL_GESTURE &&
    isFullCommit(copies)
  ) {
    return { kind: 'full-heal', amount: 0, label: 'FULL HP' };
  }

  // THE SAME LADDER THE FIGHT ROLLS, through the same function it rolls it with
  // ({@link getMeldValue}) — the ladder, the element and the rounding, once.
  const rolled = getMeldValue(card, copies, ownElement);

  if (card.effect === COMBAT_SPECIAL_EFFECT.Dot) {
    const perTurn = calculateBleedPerTurn(rolled);
    return { kind: 'dot', amount: perTurn, label: `${perTurn}/turn` };
  }

  return {
    kind: 'value',
    amount: rolled,
    /**
     * **A NUMBER DOES NOT REPEAT THE NAME OF THE CARD IT IS ON.**
     *
     * Units exist to say what a bare number counts — `14 dmg` and `14 HP` are different
     * things and the word is the only thing separating them. But `Shield` is the one card
     * in the deck whose unit IS its name, so every surface that draws the two together
     * printed the word twice in the same breath: the card face read `Shield` over
     * `12 shield`, the commit pill read `Shield x3` over `18 shield`, and the round result
     * read `Shield x3 -> 18 shield`. The number's unit is the least useful of the two
     * copies — the name is already there, in bigger type, saying exactly the same word.
     *
     * Case-insensitive and derived from the card rather than special-cased on an id, so a
     * future card named after its unit gets the same treatment without anyone remembering
     * this rule exists.
     */
    label:
      card.unit && card.unit.toLowerCase() !== card.name.toLowerCase()
        ? `${rolled} ${card.unit}`
        : `${rolled}`,
  };
}

/**
 * **HOW MANY CARDS ONE MOVE HAS TO CARRY TO TAKE THE ROUND OUTRIGHT.**
 *
 * A full stack — every copy of a card the pile prints ({@link EFFECT_FULL_STACK_COPIES}) — and
 * derived from it rather than typed as `4`, because under the loadout model the two numbers are
 * the same number: one side's pile is one card per suit at four copies each, so "four cards of
 * the suit I committed" and "four copies of one card" describe exactly the same hand. That is
 * what lets the round-result panel state this rule in the one number it already prints.
 *
 * It is a hand a player gets dealt about once in 360 (`EFFECT_COPY_HAND_COUNTS`), which is the
 * point: the pitch is the way to chase it, and chasing it is a real bet because the cards thrown
 * to go for it are gone whether or not the fourth copy comes back. (It read "once in ninety" for
 * as long as a pile was sixteen skill cards; the Suikamon cards share the same draws, so a full
 * stack has been four times rarer than that sentence claimed since they landed.)
 */
export const EXCHANGE_OVERWHELM_CARDS = EFFECT_FULL_STACK_COPIES;

/**
 * **IS THIS A FULL COMMIT — the one predicate, asked everywhere it matters.**
 *
 * `count >= EXCHANGE_OVERWHELM_CARDS` is a comparison anyone can write, and that was the
 * problem: it was written in six places — the exchange rule, the round-result panel, the four
 * move buttons, the explanation panel and the AI twice — and two of them compared the WRONG
 * QUANTITY. The AI compared `MeldStrength.copies`, the boosted scoring stack, so a boosted
 * three scored as a guaranteed round; the button printed the same number for the player. A
 * rule this load-bearing gets one function, and the function takes the only quantity the rule
 * has ever counted: CARDS ({@link countPlayedCards}).
 *
 * @param cardsPlayed how many SKILL cards were committed — never copies, never the boost.
 */
export function isFullCommit(cardsPlayed: number): boolean {
  return cardsPlayed >= EXCHANGE_OVERWHELM_CARDS;
}

/**
 * **THE SUIT WHOSE FULL COMMIT BUYS A CLEAN SLATE: recover.**
 *
 * Every full commit takes the exchange outright and plays at {@link EFFECT_MELD_MAX} — that is
 * true of all four suits. Recover gets three more payoffs on top of it, all at once
 * ({@link isFullRecoverCommit}): **back to 100% HP, no damage taken that turn, and every
 * negative condition cleared.**
 *
 * **This used to be a TRADE and is not one any more.** For one patch the heal suit bought the
 * bar INSTEAD of the round, on the reasoning that an overwhelm is worth most when you are
 * healthy and a full heal is worth nothing at full HP, so the two prizes wanted different
 * moments. In practice it made the rarest hand in the deck read as a downgrade on the one suit
 * a player chases it on: the block charged up gold like every other full commit and then handed
 * the round to the opponent. Four recovers now wins the round AND resets the fighter.
 *
 * **The mirror is the case worth stating.** Two full recover commits cancel under rule 1, the
 * wheel calls the mirror a tie, and {@link isFullRecoverCommit} does not ask who won — so BOTH
 * fighters walk away clean at 100%. Nobody takes the round and nobody is behind.
 *
 * Named as a constant rather than written as `=== 'recover'` in the places that ask, because
 * this is the one suit the deck treats differently and a hand-typed string is how that fact
 * gets out of step with itself.
 */
export const FULL_HEAL_GESTURE: EffectCardGesture = 'recover';

/**
 * **DOES THIS COMMIT TAKE THE ROUND OUTRIGHT?** — rule 1's real question.
 *
 * **ALL FOUR SUITS, RECOVER INCLUDED.** This used to exclude {@link FULL_HEAL_GESTURE}: the heal
 * suit's full commit bought the HP bar INSTEAD of the round, as a trade. The trade is gone —
 * four recovers now takes the round *and* resets the fighter ({@link isFullRecoverCommit}),
 * which is the payoffs together rather than a choice between them.
 *
 * So this is `isFullCommit` and nothing else, and the two functions are deliberately kept apart
 * rather than collapsed: they answer different questions of the same hand ("did it take the
 * round" and "does it reset the fighter"), three of the four suits answer only the first, and a
 * caller that wants one of them must not silently get the other. `action` stays in the
 * signature for the same reason — every call site names the suit it is asking about, and a
 * suit-blind predicate is how the panel came to claim four recovers "took the round" back when
 * they did not (`gestureExchange.cardsOverwhelmed`).
 *
 * **THE CRIT NO LONGER ASKS THIS ONE.** It used to, while the two were the same question. A
 * heal to full is a DESTINATION, not an amount — 150% of "back to full" is still back to full —
 * so the engine gates its automatic crit on {@link isFullRecoverCommit} being false instead, and
 * stamps 💥 only where there is a magnitude for the multiplier to act on.
 */
export function overwhelmsExchange(
  action: EffectCardGesture,
  cardsPlayed: number
): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- see the note above on `action`.
  void action;
  return isFullCommit(cardsPlayed);
}

/**
 * **IS THIS THE RECOVER SUIT'S FULL COMMIT — the hand that buys a CLEAN SLATE?**
 *
 * Every full commit takes the round ({@link overwhelmsExchange}). {@link FULL_HEAL_GESTURE}'s
 * buys three more things on top of it, and they are ONE event rather than three rules, which is
 * why they are one predicate:
 *
 * 1. **Back to 100% HP.** A heal of exactly the MISSING HP rather than a jump to the cap, so it
 *    flows through the one line that has always settled a turn (`hp - damage + heal`).
 * 2. **NO DAMAGE THIS TURN.** Every point of damage aimed at that fighter is zeroed — the
 *    exchange's, and the bleed tick a previous turn had already scheduled. This is what makes it
 *    a clean slate rather than a big heal: without it, the tick landed on the restored bar and
 *    the bar was not actually full when the turn ended.
 * 3. **EVERY NEGATIVE CONDITION CLEARED** — bleed, wound, stun, and any forced or blocked
 *    gesture. The SHIELD SURVIVES: it is armour the fighter paid for, not something done to
 *    them, and a cleanse that ate it would punish the two suits for being played together.
 *
 * It used to be the heal alone, and it was named `healsToFullHp` for it. The name went with the
 * rule: a predicate called "heals to full" gating an immunity is exactly the prose-that-stopped-
 * being-true failure this layer keeps having (`docs/BATTLE_LOGIC_QA_PLAN.md` §4.3).
 *
 * **IT DOES NOT DEPEND ON WHO WON THE ROUND**, which is what makes the mirror work: two full
 * recover commits cancel under rule 1 (see {@link resolveExchangeByCards}), the wheel calls the
 * mirror a tie, and BOTH fighters walk away clean at 100%.
 *
 * **AND IT DOES NOT CRIT.** A destination has no magnitude for a multiplier to act on — 150% of
 * "back to full" is still back to full — so the engine gates its automatic full-commit crit on
 * this being false.
 */
export function isFullRecoverCommit(action: EffectCardGesture, cardsPlayed: number): boolean {
  return isFullCommit(cardsPlayed) && action === FULL_HEAL_GESTURE;
}

/**
 * **WHAT A FULL COMMIT'S BLOW IS WORTH: the move's damage × {@link FULL_COMMIT_BLOW_MULTIPLIER}.**
 *
 * The one place the bonus is applied, so the fight (`resolveTurn`) and the move button that
 * quotes the fight cannot disagree about it. It scales the DAMAGE a move landed this turn — the
 * skill and the meld, already summed and already pressured — and nothing else: no heal, no
 * condition, no bleed tick (a previous round's move landing on schedule).
 *
 * The recover suit is passed through unchanged. Its full commit is a destination rather than an
 * amount ({@link isFullRecoverCommit}), and a bonus on Reaper riding it would pay the rarest
 * hand twice. A partial commit is passed through too: the bonus is the top rung's, and only
 * the top rung's.
 *
 * Ceiled, as every other multiplier on a blow is (`getPressureMultiplier`, the crit).
 */
export function applyFullCommitBlow(
  damage: number,
  action: EffectCardGesture,
  cardsPlayed: number
): number {
  if (damage <= 0 || !isFullCommit(cardsPlayed) || action === FULL_HEAL_GESTURE) return damage;
  return Math.ceil(damage * FULL_COMMIT_BLOW_MULTIPLIER);
}

/** Which seat took an exchange — `tie` when neither did. */
export type ExchangeSide = 'challenger' | 'defender';

/**
 * WHICH RULE settled an exchange, for the log and for the panel that explains the round.
 *
 * - `wheel` — the triangle (or a stun, or a Precise Attack) decided it, and the cards changed
 *   nothing about who won.
 * - `overwhelm` — one side committed a full stack and the other did not.
 * - `more-cards` — the wheel had nothing to say and one side simply threw more cards at it.
 * - `bigger-stack` — same gesture, same number of cards, and the bigger scoring stack took it.
 */
export type ExchangeDecider = 'wheel' | 'overwhelm' | 'more-cards' | 'bigger-stack';

export interface ExchangeResolution {
  matchup: ExchangeSide | 'tie';
  decidedBy: ExchangeDecider;
}

/**
 * **WHAT THE CARDS DO TO THE OUTCOME OF AN EXCHANGE, in the two places they are allowed to.**
 *
 * Everywhere else in the game the cards set the SIZE of a result the wheel already decided.
 * These are the two exceptions, and they are stated here — pure, RNG-free, shared — so the
 * engine, the round-result panel and `/battle-cards` all read one rule instead of three copies
 * of it.
 *
 * 1. **A FULL STACK TAKES THE ROUND.** Commit {@link EXCHANGE_OVERWHELM_CARDS} cards and the
 *    exchange is yours whatever the triangle says — *unless they committed a full stack too*,
 *    in which case neither side overwhelms anything and the round resolves normally. It is the
 *    payoff the copies ladder never had: four copies used to be nothing but ×4 on a number, so
 *    the rarest hand in the deck was worth exactly as much as the second-rarest plus a bit.
 *
 *    **On all four suits.** {@link FULL_HEAL_GESTURE} takes the round like the rest and resets
 *    its owner on top ({@link isFullRecoverCommit}) — which is also what makes the recover mirror
 *    the one exchange where BOTH fighters come back clean: two full commits cancel, the mirror
 *    ties, and the reset never asks who won.
 *
 * 2. **A TIE GOES TO WHOEVER PLAYED MORE CARDS.** The wheel says nothing on a mirror, so the
 *    hands decide it: more cards committed wins, and if both threw the same number the bigger
 *    scoring stack does. Only two identical commits are a real tie, and a bare mirror still is.
 *
 * A stunned fighter commits nothing (`getTurnMeld` returns an empty meld), so neither rule can
 * ever hand a round to a fighter that never acted.
 */
/**
 * **THE ONE GESTURE WHOSE MIRROR IS NOTHING AT ALL: dodge against dodge.**
 *
 * Three of the four mirrors are events. Two attacks trade blows, two magics clash, two recovers
 * both heal — the wheel has nothing to say about who won, but the turn plainly HAPPENED. Two
 * dodges are the exception the engine has always played: `resolveTurn` prints *"Both players
 * dodge — no damage dealt"* and neither skill resolves, because neither fighter did anything to
 * the other. Both of them stepped aside.
 *
 * **The card layer used to contradict that.** A meld fires when its owner connected, and a
 * mirror that stayed tied counted as connecting for BOTH seats — so two dodges landed two
 * bleeds, two shields, two wounds and two punishes on a turn the game had just announced as
 * nothing happening. It is the only place in the deck where the cards do not size a result the
 * wheel decided but invent one it explicitly declined to give.
 *
 * So a dodge mirror that stays a real tie lands NOTHING, from either side. Note how narrow that
 * is — it is only the exchange no rule could settle:
 *
 *  • Play more cards than they did and the mirror is yours by C3; your meld fires as usual.
 *  • Play a full commit against their smaller one and it is yours by C1.
 *  • Only two IDENTICAL dodge commits — same count, same scoring stack — reach this rule, and
 *    those are the two hands that agreed to do nothing.
 *
 * **The cards are still spent** (C7): committing is the bet, and a bet on a round nobody won is
 * a bet lost by both. What changes is that a round the game calls nothing is nothing.
 */
export const DEAD_MIRROR_GESTURE: EffectCardGesture = 'dodge';

/**
 * Did this exchange resolve to NOTHING — the dead mirror above, after every card rule has had
 * its say?
 *
 * Takes the SETTLED matchup, so it can only ever be true of a tie no rule could break. Shared
 * rather than written into the engine because two other surfaces have to state it: the round
 * result explains why a hand of cards landed nothing, and `/battle-cards` teaches it.
 */
export function isDeadExchange(
  matchup: ExchangeSide | 'tie',
  challengerAction: EffectCardGesture,
  defenderAction: EffectCardGesture
): boolean {
  return (
    matchup === 'tie' &&
    challengerAction === defenderAction &&
    challengerAction === DEAD_MIRROR_GESTURE
  );
}

export function resolveExchangeByCards(
  matchup: ExchangeSide | 'tie',
  challengerAction: EffectCardGesture,
  defenderAction: EffectCardGesture,
  challengerCards: readonly EffectCardId[],
  defenderCards: readonly EffectCardId[],
  /**
   * The suit BOTH sides threw is boosted this round.
   *
   * Only the mirror tie-break below reads it, and it is one flag rather than two because the
   * rule only ever fires on a mirror — same gesture, same boost. It is not decoration: the
   * boost is capped at {@link EFFECT_MELD_MAX}, so a 4-vs-3 mirror becomes 4-vs-4 and the
   * bigger stack stops deciding it. Reading the unboosted numbers here would settle the round
   * on stacks the fight never played.
   *
   * **THE BOOST NEVER WINS A ROUND BY ITSELF.** It adds copies, not CARDS, so it cannot reach
   * {@link EXCHANGE_OVERWHELM_CARDS} and cannot touch the more-cards rule. The wheel still
   * decides who wins; the boost decides how big.
   */
  boosted = false
): ExchangeResolution {
  // EVERY CARD COMMITTED, empty slots included — `countPlayedCards` is the one definition of
  // that number, and the same one every screen prints. It used to exclude the suit's empty
  // slots, which was right while a base card landed nothing; now that it lands
  // `BASE_CARD_CHIP` a copy, a card is a card and four blanks are a full commit like any
  // other four.
  const challengerPlayed = countPlayedCards(challengerCards);
  const defenderPlayed = countPlayedCards(defenderCards);
  // `overwhelmsExchange`, not `isFullCommit`: the two agree on all four suits today, and they
  // have not always — for one patch four recovers bought the bar INSTEAD of the round. Asking
  // the rule by name is what kept that difference in one place, and what it costs is nothing.
  // See FULL_HEAL_GESTURE.
  const challengerFull = overwhelmsExchange(challengerAction, challengerPlayed);
  const defenderFull = overwhelmsExchange(defenderAction, defenderPlayed);

  // Rule 1. Both full stacks cancel: the exchange falls through to the wheel, which is the only
  // reading that keeps the rule symmetrical — a rule that broke the mirror in the challenger's
  // favour would make the seat matter, and no other rule in this game does.
  if (challengerFull !== defenderFull) {
    return { matchup: challengerFull ? 'challenger' : 'defender', decidedBy: 'overwhelm' };
  }

  // Rule 2 only ever applies to a round the wheel left even.
  if (matchup !== 'tie') return { matchup, decidedBy: 'wheel' };

  if (challengerPlayed !== defenderPlayed) {
    return {
      matchup: challengerPlayed > defenderPlayed ? 'challenger' : 'defender',
      decidedBy: 'more-cards',
    };
  }

  const challengerStack = getMeldStrength(challengerCards, challengerAction, boosted).copies;
  const defenderStack = getMeldStrength(defenderCards, defenderAction, boosted).copies;
  if (challengerStack !== defenderStack) {
    return {
      matchup: challengerStack > defenderStack ? 'challenger' : 'defender',
      decidedBy: 'bigger-stack',
    };
  }

  return { matchup: 'tie', decidedBy: 'wheel' };
}
